import '../css/ReportPage.less';
import _ from 'lodash';

import React, {useEffect} from 'react';
import {History} from 'history';

import NoMatch from '../components/NoMatch';
import Loader from '../components/WandbLoader';
import {useReport} from '../state/reports/hooks';
import * as ReportSelectors from '../state/reports/selectors';
import * as PollingActions from '../state/polling/actions';
import * as Url from '../util/url';
import {useSelector} from '../state/hooks';
import * as Urls from '../util/urls';

import {ReportViewRef} from '../state/reports/types';
import {useReportPageQuery} from '../state/graphql/reportPageQuery';
import {useDispatch, usePropsSelector} from '../state/hooks';
import * as ViewerHooks from '../state/viewer/hooks';
import ReportPageHeaderView from '../components/ReportPageHeaderView';
import ReportFooter from '../components/ReportFooter';
import {ReportAdminBar} from '../components/ReportAdminBar';
import * as ReportTypes from '../state/reports/types';
import {Viewer} from '../state/viewer/types';
import {Redirect} from 'react-router';
import {useAdminModeActive} from '../util/admin';
import {PUBLISHED_PROJECT_NAME} from '../util/constants';
import {setDocumentTitle} from '../util/document';
import {navigateTo} from '../util/history';
import {Link} from 'react-router-dom';
import {Icon} from 'semantic-ui-react';
import {useIsFeaturedReport} from '../util/reportGallery';
import {PanelBankContextProvider} from '../state/panelbank/context';
import ReportErrorBoundary from '../components/ReportErrorBoundary';
import {useTrackMaxScrollDepth, useTrackTimeSpentOnPage} from '../util/hooks';
import makeComp from '../util/profiler';
import WBSlateReduxBridge from '../components/Slate/WBSlateReduxBridge';
import {RunQueryContext} from '../state/runs/context';
import {ReportDiscussionContextProvider} from '../components/ReportDiscussionContext';
import {ReportDiscussion} from '../components/ReportDiscussion';

interface ReportPageViewPublishedWorkProps {
  match: {
    params: {
      entityName: string;
      publishedWorkNameAndID: string;
    };
  };
  history: History;
  location: Location;
}

export const ReportPageViewPublishedWork = makeComp(
  (props: ReportPageViewPublishedWorkProps) => {
    const {entityName, publishedWorkNameAndID} = props.match.params;
    let reportID = '';
    try {
      reportID = Url.parseNameAndID(publishedWorkNameAndID).id;
    } catch {
      return <NoMatch />;
    }
    return (
      <ReportPageViewInner
        entityName={entityName}
        projectName={PUBLISHED_PROJECT_NAME}
        reportID={reportID}
        history={props.history}
        projectReadOnly
        publishedWork
      />
    );
  },
  {id: 'ReportPageViewPublishedWork'}
);

interface ReportPageViewValidatorProps {
  match: {
    params: {
      entityName: string;
      projectName: string;
      reportNameAndID: string;
    };
  };
  history: History;
  location: Location;
}

const ReportPageViewValidator: React.FC<ReportPageViewValidatorProps> = makeComp(
  props => {
    const {entityName, projectName, reportNameAndID} = props.match.params;
    let reportID = '';
    try {
      reportID = Url.parseNameAndID(reportNameAndID).id;
    } catch {
      return (
        <Redirect to={Urls.reportList({entityName, projectName}) + '#404'} />
      );
    }
    return (
      <ReportErrorBoundary params={props.match.params}>
        <ReportPageView
          entityName={entityName}
          projectName={projectName}
          reportID={reportID}
          history={props.history}
        />
      </ReportErrorBoundary>
    );
  },
  {id: 'ReportPageViewValidator'}
);

interface ReportPageViewProps {
  entityName: string;
  projectName: string;
  reportID: string;
  history: History;
}

const ReportPageView: React.FC<ReportPageViewProps> = makeComp(
  props => {
    // Note(adrnswanberg): I've factored most of ReportPageView into ReportPageViewInner so
    // that we can split up useReportPageQuery and useReport into separate components.
    // Main reason being: if someone doesn't have access to a ReportPage, we don't want
    // to try to load the report spec because then stuff still start breaking.
    const reportQuery = useReportPageQuery({
      entityName: props.entityName,
      projectName: props.projectName,
    });

    const {isFeaturedReport} = useIsFeaturedReport();

    if (reportQuery.loading) {
      return <Loader />;
    }

    if (reportQuery.project == null) {
      return <NoMatch />;
    }

    return (
      <PanelBankContextProvider>
        <ReportPageViewInner
          entityName={props.entityName}
          projectName={props.projectName}
          reportID={props.reportID}
          key={props.reportID}
          history={props.history}
          projectReadOnly={reportQuery.project.readOnly}
          isFeatured={isFeaturedReport}
        />
      </PanelBankContextProvider>
    );
  },
  {id: 'ReportPageView'}
);

interface ReportPageViewInnerProps {
  entityName: string;
  projectName: string;
  history: History;
  reportID: string;
  projectReadOnly?: boolean;
  publishedWork?: boolean;
  isFeatured?: boolean;
}

const ReportPageViewInner: React.FC<ReportPageViewInnerProps> = makeComp(
  props => {
    const dispatch = useDispatch();
    useEffect(() => {
      // Don't poll on the report page for now
      dispatch(PollingActions.setBasePollInterval(0));
    }, [dispatch]);

    const viewer = ViewerHooks.useViewer();

    const reportCID = useReport(props.reportID);
    const reportViewID = usePropsSelector(
      ReportSelectors.makeReportViewIDSelector,
      reportCID
    );

    if (reportViewID.loading) {
      return <Loader />;
    }

    return (
      <ReportPageViewReady
        viewRef={reportViewID.viewID}
        viewer={viewer}
        history={props.history}
        entityName={props.entityName}
        projectName={props.projectName}
        editAccess={viewer != null && !props.projectReadOnly}
        publishedWork={props.publishedWork}
        reportID={props.reportID}
        isFeatured={props.isFeatured}
      />
    );
  },
  {id: 'ReportPageViewInner'}
);

interface ReportPageViewReadyProps {
  viewRef: ReportTypes.ReportViewRef;
  viewer?: Viewer;
  history: History;
  entityName: string;
  projectName: string;
  editAccess?: boolean;
  publishedWork?: boolean;
  reportID: string;
  isFeatured?: boolean;
}

const ReportPageViewReady: React.FC<ReportPageViewReadyProps> = makeComp(
  props => {
    useTrackTimeSpentOnPage('report', props.reportID);
    const signalPageReady = useTrackMaxScrollDepth(
      'report',
      props.reportID,
      500
    );

    useEffect(() => {
      setTimeout(signalPageReady, 5000);
    }, [signalPageReady]);

    const reportWidth = useSelector(
      ReportSelectors.getReportWidth(props.viewRef)
    );

    const admin = useAdminModeActive();

    return (
      <div
        className={`report-page page-tabs read-only report-width-${reportWidth}`}>
        {!props.publishedWork && (
          <ReportPageViewUrl viewRef={props.viewRef} history={props.history} />
        )}
        <div className="report-page-top">
          {admin && <ReportAdminBar {...props} />}
          <ReportPageHeaderView
            viewRef={props.viewRef}
            editAccess={!props.publishedWork && props.editAccess}
          />
          <ReportDiscussionContextProvider
            reportViewRef={props.viewRef}
            reportServerID={props.reportID}>
            <RunQueryContext.Provider
              value={{
                entityName: props.entityName,
                projectName: props.projectName,
                report: {id: props.reportID},
              }}>
              <WBSlateReduxBridge
                readOnly={true}
                entityName={props.entityName}
                projectName={props.projectName}
                viewId={props.reportID}
                viewRef={props.viewRef}
              />
            </RunQueryContext.Provider>
            <ReportDiscussion />
          </ReportDiscussionContextProvider>
          {props.isFeatured && (
            <div className="report-gallery-text hide-on-print">
              Published with{' '}
              <span className="heart" role="img" aria-label="heart">
                ❤️
              </span>{' '}
              on Weights &amp; Biases. Read more reports in the{' '}
              <Link to="/gallery">
                featured gallery
                <Icon name="arrow right" />
              </Link>
            </div>
          )}
          <div className="show-on-print">
            <p>
              Created with{' '}
              <span className="heart" role="img" aria-label="heart">
                ❤️
              </span>{' '}
              on Weights &amp; Biases.
            </p>
            <a href={document.location.href}>{document.location.href}</a>
          </div>
        </div>
        <ReportFooter />
      </div>
    );
  },
  {id: 'ReportPageViewReady'}
);

interface ReportPageViewUrlProps {
  viewRef: ReportViewRef;
  history: History;
}

const ReportPageViewUrl = makeComp(
  (props: ReportPageViewUrlProps) => {
    // We do a deep comparison on this selector because if the view updates,
    // we end up getting a reference change on project. This page doesn't
    // actually update the view since you can't save here. But might as well
    // mirror the edit page behavior.
    const view = useSelector(state => {
      const {project: viewProject, id, displayName} = state.views.views[
        props.viewRef.id
      ];
      return {
        project: viewProject,
        id,
        displayName,
      };
    }, _.isEqual);
    // Make sure url tracks report name
    useEffect(() => {
      if (view.id == null) {
        throw new Error('invalid state');
      }
      setDocumentTitle(`${view.displayName} | ${view.project.name}`);
      navigateTo({
        pathname: Urls.reportView({
          entityName: view.project.entityName,
          projectName: view.project.name,
          reportID: view.id,
          reportName: view.displayName,
        }),
        preserveSearch: true,
        preserveHash: true,
        replace: true,
      });
    }, [
      props.history,
      view.displayName,
      view.id,
      view.project.entityName,
      view.project.name,
    ]);

    return null;
  },
  {id: 'ReportPageViewUrl'}
);

export default ReportPageViewValidator;
